import { useEffect, useRef, useState } from "react";
import { Mic, Send, Square, AlertTriangle, Volume2, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const typeMeta = {
  note: { icon: StickyNote, label: "Note", color: "text-white/70" },
  voice: {
    icon: Volume2,
    label: "Voice",
    color: "text-[hsl(var(--green))]",
  },
  interrupt: {
    icon: AlertTriangle,
    label: "Interrupt",
    color: "text-[hsl(var(--pink))]",
  },
  subtask: { icon: StickyNote, label: "Subtask", color: "text-white/70" },
  link: { icon: StickyNote, label: "Link", color: "text-white/70" },
};

function timeLabel(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function BreadcrumbFeed({ crumbs, onAdd, onVoice }) {
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const feedRef = useRef(null);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [crumbs.length]);

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    onAdd(t, "note");
    setText("");
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, {
          type: mime || "audio/webm",
        });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 0) await onVoice(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) {
      console.error(e);
      alert("Mic access denied. Voice notes need microphone permission.");
    }
  };

  const stopRec = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
  };

  return (
    <div className="card-elevated p-6 flex flex-col h-[calc(100vh-180px)] min-h-[520px]" data-testid="breadcrumb-feed">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40">
            Train of thought
          </div>
          <h3 className="font-display text-xl font-bold mt-1">Breadcrumbs</h3>
        </div>
        <div className="text-xs font-mono text-white/40" data-testid="crumb-count">
          {crumbs.length} {crumbs.length === 1 ? "crumb" : "crumbs"}
        </div>
      </div>

      <div
        ref={feedRef}
        className="flex-1 overflow-y-auto pr-1 mb-4"
        data-testid="breadcrumb-list"
      >
        {crumbs.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center text-white/40 text-sm font-mono py-12">
            <StickyNote className="w-6 h-6 mb-3 text-white/20" />
            <p className="max-w-xs leading-relaxed">
              Drop notes as they happen. Voice or text — we&apos;ll keep them
              in order.
            </p>
          </div>
        ) : (
          <div className="timeline">
            {crumbs.map((c) => {
              const meta = typeMeta[c.type] || typeMeta.note;
              const Icon = meta.icon;
              return (
                <div
                  key={c.id}
                  className={`timeline-item type-${c.type} crumb-in`}
                  data-testid="breadcrumb-feed-card"
                >
                  <span className="dot" />
                  <div className="card-soft p-4 hover:border-white/20 transition">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] ${meta.color}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </div>
                      <div className="text-[10px] font-mono text-white/30">
                        {timeLabel(c.created_at)}
                      </div>
                    </div>
                    <div className="text-sm leading-relaxed text-white/85 whitespace-pre-wrap">
                      {c.content}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t border-white/5 pt-4">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Drop a thought… (⌘+Enter to send)"
          className="bg-black/30 border-white/10 text-white placeholder:text-white/30 resize-none rounded-xl mb-2"
          rows={2}
          data-testid="crumb-text-input"
        />
        <div className="flex gap-2">
          <Button
            onClick={recording ? stopRec : startRec}
            className={`flex-1 rounded-xl h-11 border ${
              recording
                ? "bg-[hsl(var(--pink))]/15 border-[hsl(var(--pink))]/50 text-[hsl(var(--pink))] rec-pulse"
                : "bg-white/5 hover:bg-white/10 border-white/10 text-white"
            }`}
            data-testid="voice-record-button"
          >
            {recording ? (
              <>
                <Square className="w-4 h-4 mr-2" /> Stop
              </>
            ) : (
              <>
                <Mic className="w-4 h-4 mr-2" /> Voice
              </>
            )}
          </Button>
          <Button
            onClick={submit}
            disabled={!text.trim()}
            className="flex-1 bg-[hsl(var(--green))]/15 hover:bg-[hsl(var(--green))]/25 border border-[hsl(var(--green))]/40 text-[hsl(var(--green))] disabled:opacity-40 rounded-xl h-11"
            data-testid="crumb-submit-button"
          >
            <Send className="w-4 h-4 mr-2" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
}
